/* eslint @typescript-eslint/strict-boolean-expressions: off */

import Link from "next/link";
import React from "react";
import styles from "gui/widgets/entries.module.css";
import { ButtonOld } from "components/ButtonOld/ButtonOld";
import { ButtonGroup } from "components/ButtonOld/ButtonGroup";
import { Center } from "gui/center/center";
import { DashboardCreateButton } from "gui/dashboard/create";
import { DialogButton } from "gui/dialog/dialog";
import { Entry } from "lib/entries/types";
import { FullDashboard } from "lib/entries/types";
import { EntriesRequestFilters } from "lib/entries/types";
import { Icon } from "gui/icon/icon";
import { InputText } from "gui/input/input";
import { InsertVisualizationWidgetMutation } from "lib/types/graphql";
import { InsertVisualizationWidgetMutationVariables } from "lib/types/graphql";
import { Loading } from "gui/loading/loading";
import { QueryVisual } from "lib/visuals/types";
import { RemoveVisualizationWidgetsMutation } from "lib/types/graphql";
import { RemoveVisualizationWidgetsMutationVariables } from "lib/types/graphql";
import { Session } from "lib/users/types";
import { Visual } from "lib/visuals/types";
import { apolloCore } from "lib/apollo/apollo";
import { createWidgetOptions } from "lib/widgets/widgets";
import { entryIcon } from "lib/entries/entries";
import { entryPath } from "lib/links/links";
import { entrySlug } from "lib/entries/entries";
import { formatVisualName } from "lib/visuals/visuals";
import { isDashboard } from "lib/entries/entries";
import { isQuery } from "lib/entries/entries";
import { preventDefault } from "lib/events/events";
import { useEntries } from "lib/entries/hooks";
import { useErrorBoundary } from "lib/hooks/use-error-boundary";
import { useRequiredSession } from "gui/session/session";
import { useSessionHasPaidPlan } from "gui/session/session";
import { visualIcon } from "lib/visuals/visuals";
import { EntryFilter } from "lib/entries/types";
import { insertVisualizationWidget } from "gui/widgets/graphql";
import { removeVisualizationWidgets } from "gui/widgets/graphql";
import { Tooltip } from "components/Tooltip/Tooltip";
import { Button } from "components/Button/Button";

export const WidgetsEntriesButton: React.FC<{
  item: FullDashboard | QueryVisual;
  refresh?: () => void;
  disabled?: boolean;
}> = (props) => {
  const label = isDashboardItem(props.item)
    ? "Add visualization"
    : "Add to dashboard";

  const dashboard: FullDashboard = props.item as FullDashboard;

  if (dashboard && dashboard.is_archived) {
    return (
      <Tooltip
        position="below-center"
        label="Archived dashboards cannot be updated"
      >
        <Button size="M" disabled theme="tertiary">
          {label}
        </Button>
      </Tooltip>
    );
  }

  return (
    <DialogButton label={label} disabled={props.disabled} size="lg">
      {(onDismiss) => <WidgetsEntriesDialog {...props} onDismiss={onDismiss} />}
    </DialogButton>
  );
};

const WidgetsEntriesDialog: React.FC<{
  item: FullDashboard | QueryVisual;
  refresh?: () => void;
  onDismiss: () => void;
}> = (props) => {
  const slug = isDashboardItem(props.item) ? "queries" : "dashboards";
  const [
    entriesRequestFilters,
    setEntriesRequestFilters,
  ] = React.useState<EntriesRequestFilters>({
    page_size: 25,
  });
  const [loading, setLoading] = React.useState(false);

  type FilterState = EntryFilter | undefined;
  const [filter, setFilter] = React.useState<FilterState>("authored");
  const authored = filter === "authored";
  const placeholder = `Search ${authored ? "your" : "everyone's"} ${slug}...`;

  const entries = useEntries(slug, entriesRequestFilters, filter);
  const session = useRequiredSession();
  const isPro = useSessionHasPaidPlan();
  const setError = useErrorBoundary();

  const toggleFilter = () => {
    setFilter((prev) => (prev === "authored" ? undefined : "authored"));
  };

  const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const q = event.target.value;
    setEntriesRequestFilters((prev) => ({ ...prev, q }));
  };

  const onToggleVisualizationWidget = (
    entry: FullDashboard,
    visual: QueryVisual
  ) => {
    setLoading(true);
    callToggleVisualizationWidgets(entry, visual, session)
      .then(() => entries.mutate())
      .then(() => props.refresh?.())
      .then(() => setLoading(false))
      .catch(setError);
  };

  if (entries.error) {
    throw entries.error;
  }

  return (
    <form onSubmit={preventDefault} className={styles.form}>
      <InputText
        type="search"
        aria-label={`Search ${slug}`}
        placeholder={placeholder}
        value={entriesRequestFilters.q ?? ""}
        onChange={onSearch}
      />
      <DialogEntriesResults
        item={props.item}
        entries={entries.data?.results}
        onToggle={onToggleVisualizationWidget}
        loading={loading}
      />
      <ButtonGroup>
        <ButtonOld onClick={props.onDismiss} size="sm" color2>
          Done
        </ButtonOld>
        <DashboardCreateButton
          redirect={isDashboardItem(props.item)}
          refresh={entries.mutate}
        />
        {isDashboardItem(props.item) && (
          <ButtonOld onClick={toggleFilter} size="sm" color2 light={authored}>
            Show queries from other users
            {!authored && <Icon icon="check" />}
          </ButtonOld>
        )}
      </ButtonGroup>
      {isPro && (
        <p className={styles.warning}>
          Adding a visualization from a <strong>private query</strong> to a
          public dashboard will make the visualization available to everyone. If
          you wish to keep the visualization private, make the dashboard private
          first.
        </p>
      )}
    </form>
  );
};

const DialogEntriesResults: React.FC<{
  item: FullDashboard | QueryVisual;
  entries?: Entry[];
  onToggle: (entry: FullDashboard, visual: QueryVisual) => void;
  loading: boolean;
}> = (props) => {
  const { item } = props;

  if (!props.entries) {
    return (
      <div className={styles.entries}>
        <Loading />
      </div>
    );
  }

  if (props.entries.length === 0) {
    return (
      <div className={styles.entries}>
        <Center>No results found.</Center>
      </div>
    );
  }

  if (isDashboardItem(item)) {
    return (
      <ul className={styles.entries}>
        {props.entries
          .filter(isQuery)
          .map((e) =>
            (e.visualizations ?? []).map((v) => (
              <DialogEntriesResultsItem
                key={`${e.id}-${v.id}`}
                entry={e}
                dashboard={item}
                visual={v}
                onToggle={props.onToggle}
                loading={props.loading}
              />
            ))
          )}
      </ul>
    );
  }

  return (
    <ul className={styles.entries}>
      {props.entries.filter(isDashboard).map((e) => (
        <DialogEntriesResultsItem
          key={e.id}
          entry={e}
          dashboard={e}
          visual={item}
          onToggle={props.onToggle}
          loading={props.loading}
        />
      ))}
    </ul>
  );
};

const DialogEntriesResultsItem: React.FC<{
  entry: Entry;
  dashboard: FullDashboard;
  visual: QueryVisual;
  onToggle: (entry: FullDashboard, visual: QueryVisual) => void;
  loading: boolean;
}> = (props) => {
  const matching = findMatchingWidgets(props.dashboard, props.visual);
  const selected = matching.length > 0;

  const icon = props.entry.is_private
    ? "lock"
    : isQuery(props.entry)
    ? visualIcon(props.visual)
    : entryIcon(entrySlug(props.entry));

  return (
    <li>
      <p>
        <Icon icon={icon} />
        <Link href={entryPath(props.entry)}>
          <a>
            @{props.entry.owner.handle} / {props.entry.name}
          </a>
        </Link>
        {isQuery(props.entry) && (
          <span className={styles.visual}>
            {formatVisualName(props.visual)}
          </span>
        )}
      </p>
      <button
        type="button"
        onClick={props.onToggle.bind(null, props.dashboard, props.visual)}
        disabled={props.loading}
        aria-selected={selected}
        aria-busy={props.loading}
        aria-live="polite"
      >
        {selected ? "Added" : "Add"}
      </button>
    </li>
  );
};

const isDashboardItem = (
  item: FullDashboard | QueryVisual
): item is FullDashboard => {
  return "slug" in item;
};

// Check dashboard contains a widget with a specific visualization.
// (A dashboard may have many widgets with the same visualization).
const findMatchingWidgets = (dashboard: FullDashboard, visual: Visual) => {
  return (dashboard.visualization_widgets ?? []).filter((w) => {
    return w.visualization?.id === visual.id;
  });
};

// Add a new dashboard widget for a visualization,
// or remove all widgets for that visualization if any exist.
const callToggleVisualizationWidgets = async (
  dashboard: FullDashboard,
  visual: Visual,
  session: Session
) => {
  const widgets = findMatchingWidgets(dashboard, visual);

  if (!visual.id) {
    throw new Error("missing widget visualization id");
  }

  if (widgets.length > 0) {
    return await apolloCore.mutate<
      RemoveVisualizationWidgetsMutation,
      RemoveVisualizationWidgetsMutationVariables
    >({
      mutation: removeVisualizationWidgets,
      context: { session },
      fetchPolicy: "no-cache",
      variables: { widgets: widgets.map((w) => w.id) },
    });
  }

  return await apolloCore.mutate<
    InsertVisualizationWidgetMutation,
    InsertVisualizationWidgetMutationVariables
  >({
    mutation: insertVisualizationWidget,
    context: { session },
    fetchPolicy: "no-cache",
    variables: {
      object: {
        dashboard_id: dashboard.id,
        visualization_id: visual.id,
        options: createWidgetOptions(),
      },
    },
  });
};
